import type { NodePropertySchema, NodeProperty } from '../../../types/node-properties';
import { domains } from './templates';

/**
 * Access Financials Node Schema
 *
 * GraphQL-based integration with Access Financials API
 * Uses built-in Access Identity authentication with APIM subscription key
 *
 * Supports two modes:
 * - Template Mode: Select from pre-built queries with cascading dropdowns (Domain → Resource → Action)
 * - Custom Mode: Write your own GraphQL queries
 */

// Build domain options from templates
const domainOptions = Object.entries(domains).map(([key, domain]) => ({
  value: key,
  name: domain.name,
  description: domain.description,
}));

// Build resource properties - one for each domain with displayOptions
const resourceProperties: NodeProperty[] = Object.entries(domains).map(([domainKey, domain]) => ({
  key: 'resource',
  label: 'Resource',
  type: 'options' as const,
  required: true,
  default: Object.keys(domain.resources)[0] || '',
  options: Object.entries(domain.resources).map(([resourceKey, resource]) => ({
    value: resourceKey,
    name: resource.name,
    description: resource.description,
  })),
  description: `The ${domain.name} resource to interact with`,
  displayOptions: {
    show: {
      queryMode: ['template'],
      domain: [domainKey],
    },
  },
}));

// Build action properties - one for each domain+resource combination with displayOptions
const actionProperties: NodeProperty[] = Object.entries(domains).flatMap(([domainKey, domain]) =>
  Object.entries(domain.resources).map(([resourceKey, resource]) => ({
    key: 'action',
    label: 'Action',
    type: 'options' as const,
    required: true,
    default: Object.keys(resource.actions)[0] || '',
    options: Object.entries(resource.actions).map(([actionKey, action]) => ({
      value: actionKey,
      name: action.name,
      description: action.description,
    })),
    description: `Action to perform on ${resource.name}`,
    displayOptions: {
      show: {
        queryMode: ['template'],
        domain: [domainKey],
        resource: [resourceKey],
      },
    },
  }))
);

const schema: NodePropertySchema = {
  nodeType: 'access-financials',
  version: '1.0.0',

  properties: [
    // ==================================================================
    // ENVIRONMENT
    // ==================================================================
    {
      key: 'environment',
      label: 'Environment',
      type: 'options',
      required: true,
      default: 'preprod',
      options: [
        { value: 'preprod', name: 'Pre-Production' },
        { value: 'staging', name: 'Staging' },
        { value: 'demo', name: 'Demo' },
        { value: 'production', name: 'Production' },
      ],
      description: 'Select the Access Financials environment',
    },

    // ==================================================================
    // INSTANCE CONFIGURATION
    // ==================================================================
    {
      key: 'instanceId',
      label: 'Instance ID',
      type: 'string',
      required: true,
      placeholder: 'Select an instance or enter ID',
      description: 'The Financials database instance ID (X-Instance-Id header). Select from available instances or enter manually.',
      modeToggle: {
        enabled: true,
        defaultMode: 'text', // Default to manual entry - System endpoint requires admin permissions for instance discovery
        loadOptions: {
          method: 'getInstances',
          dependsOn: ['environment'],
          cacheResults: true,
          cacheTtl: 300000, // 5 minutes - instances don't change often
          requiresCredential: false, // Uses system credentials, not user-selected
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // ==================================================================
    // QUERY MODE SELECTION
    // ==================================================================
    {
      key: 'queryMode',
      label: 'Query Mode',
      type: 'options',
      default: 'template',
      options: [
        { value: 'template', name: 'Use Template', description: 'Select from pre-built queries' },
        { value: 'custom', name: 'Custom GraphQL', description: 'Write your own query' },
      ],
      description: 'Choose between pre-built templates or custom GraphQL queries',
    },

    // ==================================================================
    // TEMPLATE SELECTION - CASCADING DROPDOWNS (Template Mode)
    // Domain → Resource → Action
    // ==================================================================
    {
      key: 'domain',
      label: 'Domain',
      type: 'options',
      required: true,
      default: 'debtors',
      options: domainOptions,
      description: 'The Financials domain to interact with',
      displayOptions: {
        show: {
          queryMode: ['template'],
        },
      },
    },
    // Resource dropdowns - one per domain, shown conditionally
    ...resourceProperties,
    // Action dropdowns - one per domain+resource, shown conditionally
    ...actionProperties,

    // ==================================================================
    // PAGINATION CONTROLS (Template Mode - for list operations)
    // ==================================================================
    {
      key: 'paginationMode',
      label: 'Pagination',
      type: 'options',
      default: 'single',
      options: [
        { value: 'single', name: 'Single Page', description: 'Fetch one page of results' },
        { value: 'all', name: 'Fetch All Pages', description: 'Automatically fetch all pages (up to max)' },
      ],
      description: 'How to handle paginated results',
      displayOptions: {
        show: {
          queryMode: ['template'],
        },
      },
    },
    {
      key: 'pageSize',
      label: 'Page Size',
      type: 'number',
      default: 50,
      description: 'Number of records to fetch per page (max 100)',
      displayOptions: {
        show: {
          queryMode: ['template'],
        },
      },
      typeOptions: {
        minValue: 1,
        maxValue: 100,
      },
    },
    {
      key: 'maxPages',
      label: 'Maximum Pages',
      type: 'number',
      default: 10,
      description: 'Maximum number of pages to fetch (safety limit)',
      displayOptions: {
        show: {
          queryMode: ['template'],
          paginationMode: ['all'],
        },
      },
      typeOptions: {
        minValue: 1,
        maxValue: 100,
      },
    },

    // ==================================================================
    // GRAPHQL OPERATION (Custom Mode)
    // ==================================================================
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      default: 'query',
      options: [
        { name: 'Query', value: 'query' },
        { name: 'Mutation', value: 'mutation' },
      ],
      description: 'Type of GraphQL operation to perform',
      displayOptions: {
        show: {
          queryMode: ['custom'],
        },
      },
    },
    {
      key: 'query',
      label: 'GraphQL Query',
      type: 'code',
      required: true,
      default: '',
      placeholder: `query ListCustomers($first: Int, $after: String) {
  debtors {
    masterRecords {
      customers(first: $first, after: $after) {
        pageInfo {
          hasNextPage
          endCursor
        }
        items {
          customerDetails {
            code
            name
          }
        }
      }
    }
  }
}`,
      description: 'GraphQL query or mutation. In template mode, this is auto-populated when you select a template.',
      typeOptions: {
        language: 'graphql',
        rows: 16,
        graphqlSchemaPath: 'shared/nodes/access-integrations/access-financials/schema.graphql',
      },
    },
    {
      key: 'variables',
      label: 'Variables',
      type: 'code',
      default: '{}',
      placeholder: `{
  "first": 50,
  "after": null,
  "code": "{{ $node["NodeName"].output.data.code }}"
}`,
      description: 'GraphQL variables as JSON. In template mode, pagination is handled automatically.',
      typeOptions: {
        language: 'json',
        rows: 6,
      },
    },

    // ==================================================================
    // CUSTOM HEADERS
    // ==================================================================
    {
      key: 'sendHeaders',
      label: 'Custom Headers',
      type: 'boolean',
      default: false,
      description: 'Whether to send additional custom headers',
    },
    {
      key: 'headerParameters',
      label: 'Header Parameters',
      type: 'fixedCollection',
      typeOptions: {
        multipleValues: true,
      },
      displayOptions: {
        show: {
          sendHeaders: [true],
        },
      },
      default: {},
      placeholder: 'Add Header',
      fixedCollectionOptions: [
        {
          name: 'parameters',
          displayName: 'Header',
          values: [
            {
              key: 'name',
              label: 'Name',
              type: 'string',
              default: '',
              placeholder: 'X-Custom-Header',
              required: true,
            },
            {
              key: 'value',
              label: 'Value',
              type: 'string',
              default: '',
              placeholder: 'header-value',
            },
          ],
        },
      ],
    },

    // ==================================================================
    // ADVANCED OPTIONS (Settings tab)
    // ==================================================================
    {
      key: 'timeout',
      label: 'Timeout (ms)',
      type: 'number',
      section: 'settings',
      default: 30000,
      description: 'Request timeout in milliseconds',
      typeOptions: {
        minValue: 1000,
        maxValue: 300000,
      },
    },
    {
      key: 'fullResponse',
      label: 'Include Full Response',
      type: 'boolean',
      section: 'settings',
      default: false,
      description: 'Return full response including headers and status code',
    },
  ],

  metadata: {
    author: 'Evo E2E Team',
    documentation: 'https://confluence.accessgroup.cloud/display/ACCESSAPI/Financials+API',
  },
};

export default schema;
